import type {FontData} from '@react-three/drei'

/**
 * On-demand cache for the 3D typeface JSONs (`/fonts/3d/...`). The full
 * catalog is ~180 MB, so fonts are fetched lazily per (family, variant)
 * actually rendered — never preloaded in bulk (a module-level preload-all in
 * `Text3DMeshV2` previously pulled the whole catalog on the marketing pages).
 *
 * The pending tracker lets the render/export paths wait until every mounted
 * 3D text has its font loaded AND committed to the three scene, so a BE
 * thumbnail/full render is never captured with missing text.
 */

const fontPromises = new Map<string, Promise<FontData>>()
const loadedFonts = new Map<string, FontData>()

export const getLoadedText3DFont = (url: string): FontData | null => loadedFonts.get(url) ?? null

export const loadText3DFont = (url: string): Promise<FontData> => {
  let promise = fontPromises.get(url)
  if (!promise) {
    promise = fetch(url).then(async (res) => {
      if (!res.ok) throw new Error(`3D font fetch failed: ${res.status}`)
      const data = (await res.json()) as FontData
      loadedFonts.set(url, data)
      return data
    })
    // Drop failed loads from the cache so a later mount can retry.
    promise.catch(() => fontPromises.delete(url))
    fontPromises.set(url, promise)
  }
  return promise
}

// Pending = mounted 3D texts whose font hasn't loaded/committed yet.
let pendingCount = 0
let waiters: (() => void)[] = []

/** Register a pending 3D text. Returns a release fn (idempotent). */
export const trackText3DFontPending = (): (() => void) => {
  pendingCount++
  let released = false
  return () => {
    if (released) return
    released = true
    pendingCount--
    if (pendingCount === 0) {
      const toNotify = waiters
      waiters = []
      for (const notify of toNotify) notify()
    }
  }
}

const PENDING_TIMEOUT_MS = 30000

/**
 * Resolves once no mounted 3D text is waiting for its font (or after a safety
 * timeout, so a stuck download can't hang a render). Never rejects.
 */
export const waitForText3DFonts = (): Promise<void> => {
  if (pendingCount === 0) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, PENDING_TIMEOUT_MS)
    waiters.push(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}
